import { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  documentId,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { db } from "../../../lib/firebase";
import { DataTable, type Column } from "../../../components/DataTable";
import { useInviteToEvent } from "../../../hooks/useUserManagement";
import { useSnackbar } from "../../../context/SnackbarContext";
import { chunkArray } from "@event-planner/shared";
import type {
  Event,
  EventParticipant,
  Role,
} from "@event-planner/shared";

interface ParticipationRow {
  eventId: string;
  eventName: string;
  role: Role;
  status: string;
}

interface EventsTabProps {
  personId: string;
  personEmail: string;
}

export function EventsTab({ personId, personEmail }: EventsTabProps) {
  const { showSuccess, showError } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<ParticipationRow[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [allEvents, setAllEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const { invite, loading: adding } = useInviteToEvent();

  // Fetch participations for this person, then resolve event names.
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const participantsSnap = await getDocs(
        query(
          collection(db, "eventParticipants"),
          where("personId", "==", personId),
        ),
      );
      const participants = participantsSnap.docs.map(
        (d) => ({ id: d.id, ...d.data() }) as EventParticipant,
      );

      if (participants.length === 0) {
        setRows([]);
        return;
      }

      const eventIds = [...new Set(participants.map((p) => p.eventId))];
      const chunks = chunkArray(eventIds, 30);
      const eventSnaps = await Promise.all(
        chunks.map((chunk) =>
          getDocs(
            query(
              collection(db, "events"),
              where(documentId(), "in", chunk),
            ),
          ),
        ),
      );
      const eventMap = new Map<string, string>();
      for (const snap of eventSnaps) {
        for (const d of snap.docs) {
          eventMap.set(d.id, (d.data() as Event).name);
        }
      }

      setRows(
        participants.map((p) => ({
          eventId: p.eventId,
          eventName: eventMap.get(p.eventId) ?? p.eventId,
          role: p.role,
          status: p.status,
        })),
      );
    } catch (err) {
      showError(
        err instanceof Error
          ? `Failed to load participations: ${err.message}`
          : "Failed to load participations",
      );
    } finally {
      setLoading(false);
    }
  }, [personId, showError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch all events for the "Add to Event" dialog
  const openDialog = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, "events"));
      setAllEvents(
        snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Event),
      );
    } catch {
      // Non-fatal, dialog opens with empty list
    }
    setDialogOpen(true);
  }, []);

  async function handleAddToEvent() {
    if (!selectedEventId) return;
    try {
      const result = await invite({
        email: personEmail,
        eventId: selectedEventId,
      });
      if (result.alreadyMember) {
        showSuccess("Person is already a participant of this event");
      } else {
        showSuccess("Person added to event");
      }
      setDialogOpen(false);
      setSelectedEventId("");
      fetchData();
    } catch (err) {
      showError(
        err instanceof Error ? err.message : "Failed to add to event",
      );
    }
  }

  const columns: Column<ParticipationRow>[] = useMemo(
    () => [
      {
        id: "eventName",
        label: "Event",
        render: (row) => row.eventName,
      },
      {
        id: "role",
        label: "Role",
        render: (row) => (
          <Chip
            label={row.role}
            size="small"
            sx={{ textTransform: "capitalize" }}
          />
        ),
      },
      {
        id: "status",
        label: "Status",
        render: (row) => (
          <Chip
            label={row.status}
            size="small"
            color={
              row.status === "active"
                ? "success"
                : row.status === "pending"
                  ? "warning"
                  : "default"
            }
            variant="outlined"
            sx={{ textTransform: "capitalize" }}
          />
        ),
      },
    ],
    [],
  );

  // Filter out events the person is already actively in for the dialog
  const availableEvents = allEvents.filter(
    (e) => !rows.some((r) => r.eventId === e.id && r.status === "active"),
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 2,
        }}
      >
        <Button variant="outlined" onClick={openDialog}>
          Add to Event
        </Button>
      </Box>

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        emptyMessage="Not a participant of any events."
        ariaLabel="Person event participations"
        getRowKey={(row) => row.eventId}
      />

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add to Event</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              select
              label="Event"
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
              fullWidth
            >
              {availableEvents.length === 0 ? (
                <MenuItem value="" disabled>
                  No events available
                </MenuItem>
              ) : (
                availableEvents.map((e) => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.name}
                  </MenuItem>
                ))
              )}
            </TextField>
            {/* Role is determined by the person's global role, no per-event override */}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddToEvent}
            disabled={!selectedEventId || adding}
            startIcon={adding ? <CircularProgress size={18} /> : undefined}
          >
            {adding ? "Adding..." : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
